import { ColumnDef } from "@tanstack/react-table";
import { toFormat } from "@/lib/dateHelpers";
import { formatNotePriority, formatNoteVisibility } from "./noteHelpers";
import { buildIconTextColumn } from "@/components/Table/buildIconTextColumn";
import { buildSourceColumn } from "@/components/Table/buildSourceColumn";
import { buildExpandableCountColumn } from "@/components/Table/buildExpandableCountColumn";
import { CRMNote } from "@/types";
import { NoteVisibility } from "@/types";

type NotesTableVariant = "top-level" | "details-panel";

function renderVisibilityCell(visibility: NoteVisibility): React.ReactNode {
  return <span>{formatNoteVisibility(visibility)}</span>;
}

export function buildNotesTableColumns(
  variant: NotesTableVariant = "top-level",
  getParentLink?: (note: CRMNote) => string | null | undefined,
  options?: {
    referencesEnabled?: boolean;
    navigate?: (to: string) => void;
    currentPathname?: string;
  }
): ColumnDef<CRMNote>[] {
  const columns: ColumnDef<CRMNote>[] = [];

  columns.push({
    accessorKey: "displayTitle",
    header: "Title",
    size: 240,
    cell: ({ row }) => {
      const displayTitle = row.original.displayTitle || row.original.title || "";
      return displayTitle || <span className="opacity-20">--</span>;
    },
    meta: {
      sortType: "text",
      headerClassName: "hide-header-in-column"
    }
  });

  columns.push({
    accessorKey: "content",
    header: "Content",
    size: 300,
    cell: ({ row }) => {
      const content = row.original.content || "";
      const truncated = content.length > 250 ? `${content.substring(0, 250)}...` : content;
      return truncated || <span className="opacity-20">--</span>;
    },
    meta: {
      sortType: "text",
      headerClassName: "hide-header-in-column"
    }
  });

  if (variant === "top-level") {
    columns.push(
      buildSourceColumn<CRMNote>({
        accessorKey: "source",
        header: "Source",
        size: 240,
        sortType: "text",
        getParentLink: getParentLink || (() => null),
        getParentType: (note) => note.notableType || null,
        navigate: options?.navigate,
        currentPathname: options?.currentPathname
      })
    );
  }

  if (options?.referencesEnabled) {
    columns.push(
      buildExpandableCountColumn<CRMNote>({
        accessorKey: "references",
        header: "References",
        getCount: (row) => row.references?.length || 0,
        size: 110,
        showExpandIcon: variant === "top-level"
      })
    );
  }

  columns.push(
    buildIconTextColumn<CRMNote>({
      accessorKey: "priority",
      header: "Priority",
      size: 120,
      sortType: "text",
      getText: (note) => {
        return formatNotePriority(note.priority);
      },
      getIcon: (note) => {
        if (note.priorityExpirationDate) {
          const formattedDate = toFormat(note.priorityExpirationDate, "MMM dd, yyyy");
          if (!formattedDate) {
            return null;
          }
          return {
            name: "schedule",
            size: 20,
            className: "text-sys-brand-on-surface-variant",
            tooltip: {
              supportingText: `Auto Expires ${formattedDate}`,
              variant: "plain",
              color: "black",
              positioning: "fixed"
            }
          };
        }
        return null;
      }
    })
  );

  columns.push({
    accessorKey: "visibility",
    header: "Visibility",
    size: 100,
    cell: ({ row }) => renderVisibilityCell(row.original.visibility),
    meta: {
      sortType: "text"
    }
  });

  columns.push({
    accessorKey: "authorName",
    header: "Author",
    size: 150,
    cell: ({ row }) => {
      const authorName = row.original.authorName || "";
      return authorName || <span className="opacity-20">--</span>;
    },
    meta: {
      sortType: "text"
    }
  });

  columns.push({
    accessorKey: "updatedAt",
    header: "Modified",
    size: 100,
    accessorFn: (row) => row.updatedAt || row.createdAt,
    cell: ({ row }) => {
      const date = row.original.updatedAt || row.original.createdAt;
      if (!date) {
        return <span className="opacity-20">--</span>;
      }
      const formatted = toFormat(date, "MMM dd, yyyy");
      return formatted || <span className="opacity-20">--</span>;
    },
    meta: {
      sortType: "date"
    }
  });

  return columns;
}
